import { IconType } from "react-icons";
import {
  LuClock,
  LuFileText,
  LuInfo,
  LuSquareCheckBig,
  LuWallet,
} from "react-icons/lu";
import { throwUa } from "./errors";
import { supabase } from "./supabase";
import { AppNotification, NotificationType } from "../models/notification";
import { ProjectStatus } from "../models/project";

export const NOTIFICATION_META: Record<
  NotificationType,
  { tint: string; label: string; icon: IconType }
> = {
  status: { tint: "bg-sky-500/10 text-sky-400", label: "Статус", icon: LuClock },
  bonus: { tint: "bg-purple-500/10 text-purple-400", label: "Бонус", icon: LuWallet },
  contract: {
    tint: "bg-green-500/10 text-green-400",
    label: "Договір",
    icon: LuSquareCheckBig,
  },
  message: { tint: "bg-orange-500/10 text-orange-400", label: "Повідомлення", icon: LuFileText },
  system: { tint: "bg-neutral-500/10 text-neutral-300", label: "Система", icon: LuInfo },
};

export const fetchNotifications = async (
  limit: number | null = null
): Promise<AppNotification[]> => {
  const { data: auth } = await supabase.auth.getSession();
  const userId = auth.session?.user.id;
  if (!userId) return [];

  // Staff can read every notification (message history), so scope to own.
  let query = supabase
    .from("notifications")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (limit) query = query.limit(limit);

  const { data, error } = await query;
  if (error) throwUa(error);
  return (data ?? []) as AppNotification[];
};

export const markNotificationRead = async (id: number): Promise<void> => {
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("id", id)
    .is("read_at", null);
  if (error) throwUa(error);
};

export const markAllNotificationsRead = async (): Promise<void> => {
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .is("read_at", null);
  if (error) throwUa(error);
};

const STATUS_TO_TYPE: Partial<Record<ProjectStatus, NotificationType>> = {
  contract: "contract",
  quote: "message",
  in_progress: "status",
  completed: "bonus",
};

const STATUS_LABELS: Partial<Record<ProjectStatus, string>> = {
  contract: "Підписано договір",
  quote: "Підготовлено КП",
  in_progress: "Проєкт в роботі",
  completed: "Проєкт реалізовано",
};

export type ProjectNotificationKind = "status" | "bonus" | "comment";

export const insertProjectNotification = async (
  p: {
    id: string;
    name: string;
    status: ProjectStatus;
    bonus_amount: number | null;
    owner_id: string;
    manager_comment?: string | null;
  },
  kind: ProjectNotificationKind
): Promise<void> => {
  const type: NotificationType =
    kind === "status"
      ? STATUS_TO_TYPE[p.status] ?? "system"
      : kind === "comment"
        ? "message"
        : "bonus";
  const title =
    kind === "status"
      ? STATUS_LABELS[p.status] ?? "Статус проєкту змінено"
      : kind === "comment"
        ? "Новий коментар менеджера"
        : "Бонус проєкту змінено";
  const body = `Проєкт «${p.name}».${
    p.bonus_amount ? ` Бонус: ${p.bonus_amount.toLocaleString("uk-UA")} €.` : ""
  }${p.manager_comment ? ` ${p.manager_comment}` : ""}`;
  const { supabase } = await import("./supabase");
  const { error } = await supabase.from("notifications").insert({
    user_id: p.owner_id,
    type,
    title,
    body,
    link: `/projects/${p.id}`,
  });
  if (error) throw error;
};

export const activityFromProjects = (
  projects: { id: string; name: string; status: ProjectStatus; bonus_amount: number | null; updated_at: string }[],
  limit = 3,
  hrefBase = "/projects"
): AppNotification[] => {
  return projects
    .filter((p) => STATUS_TO_TYPE[p.status])
    .slice(0, limit)
    .map((p, i) => ({
      id: i,
      user_id: "",
      type: STATUS_TO_TYPE[p.status] as NotificationType,
      title: STATUS_LABELS[p.status] ?? "Проєкт в роботі",
      body: `Проєкт «${p.name}».${
        p.bonus_amount ? ` Бонус: ${p.bonus_amount.toLocaleString("uk-UA")} €.` : ""
      }`,
      link: `${hrefBase}/${p.id}`,
      read_at: null,
      created_at: p.updated_at,
    }));
}